import React, { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import api, { http } from "../../utils/axios";
import { setCredentials, setLoading } from "../../store/slices/authSlice";
import {
  showApiError,
  showApiSuccess,
  showValidationError,
} from "../../utils/toast";

export default function VerifyOtpPage() {
  const logo = new URL(`../../assets/images/logo.png`, import.meta.url);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mobileFromState = location?.state?.mobile || "";

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [mobile, setMobile] = useState(mobileFromState);
  const inputsRef = useRef([]);

  const code = useMemo(() => digits.join(""), [digits]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // only digits
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (code.length !== 6) {
      showValidationError("Please enter the 6-digit OTP.");
      return;
    }
    setIsSubmitting(true);
    dispatch(setLoading(true));

    try {
      const res = await http.post("/dashboard/auth/verify-otp", {
        mobile: mobile ? Number(mobile) : undefined,
        otp: code,
      });

      const { success, data } = res || {};
      if (success && data?.token) {
        dispatch(
          setCredentials({ token: data.token, user: data.user || null })
        );
        showApiSuccess("OTP verified successfully! Welcome to dashboard.");
        navigate("/", { replace: true });
        return;
      }
      showApiError(null, "Invalid response from server.");
    } catch (err) {
      showApiError(err, "OTP verification failed. Please try again.");
    } finally {
      setIsSubmitting(false);
      dispatch(setLoading(false));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow p-6">
        <div className="w-full justify-center flex-col flex items-center">
          <img src={logo} alt="Logo" className="w-48" />
          <h1 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Login
          </h1>
        </div>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Enter the 6-digit code sent to {mobile || "your mobile"}
        </p>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile
            </label>
            <input
              type="tel"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="9876543210"
              required
            />
          </div>
          <div className="flex justify-between gap-2">
            {digits.map((d, i) => (
              <input
                key={i}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="w-12 h-12 text-center text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={digits[i]}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                ref={(el) => (inputsRef.current[i] = el)}
                autoFocus={i === 0}
              />
            ))}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Verifying...
              </>
            ) : (
              "Verify"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
